import { createWithEqualityFn } from 'zustand/traditional';

export type CableTestPortMode = 'all' | 'selection' | 'custom';

export type CableTestActionType = 'start' | 'history';

export type CableTestActionStatus = 'pending' | 'success' | 'error';

export interface CableTestActionEntry {
  action: CableTestActionType;
  status: CableTestActionStatus;
  message?: string;
  timestamp: string;
}

interface CableTestDeviceState {
  portMode: CableTestPortMode;
  priority: number;
  customPorts: number[];
  lastAction?: CableTestActionEntry;
}

interface CableTestStoreState {
  devices: Record<number, CableTestDeviceState>;
  setPortMode: (deviceId: number, mode: CableTestPortMode) => void;
  setPriority: (deviceId: number, priority: number) => void;
  setCustomPorts: (deviceId: number, ports: number[]) => void;
  pushActionStatus: (
    deviceId: number,
    entry: CableTestActionEntry,
  ) => void;
  resetDevice: (deviceId: number) => void;
}

const DEFAULT_STATE: CableTestDeviceState = {
  portMode: 'all',
  priority: 5,
  customPorts: [],
};

export const useDeviceCableTestStore = createWithEqualityFn<CableTestStoreState>()((set) => ({
  devices: {},
  setPortMode: (deviceId, mode) =>
    set((state) => ({
      devices: {
        ...state.devices,
        [deviceId]: {
          ...(state.devices[deviceId] ?? DEFAULT_STATE),
          portMode: mode,
          customPorts:
            mode === 'custom'
              ? state.devices[deviceId]?.customPorts ?? []
              : [],
        },
      },
    })),
  setPriority: (deviceId, priority) =>
    set((state) => ({
      devices: {
        ...state.devices,
        [deviceId]: {
          ...(state.devices[deviceId] ?? DEFAULT_STATE),
          priority,
        },
      },
    })),
  setCustomPorts: (deviceId, ports) =>
    set((state) => ({
      devices: {
        ...state.devices,
        [deviceId]: {
          ...(state.devices[deviceId] ?? DEFAULT_STATE),
          customPorts: ports,
        },
      },
    })),
  pushActionStatus: (deviceId, entry) =>
    set((state) => ({
      devices: {
        ...state.devices,
        [deviceId]: {
          ...(state.devices[deviceId] ?? DEFAULT_STATE),
          lastAction: entry,
        },
      },
    })),
  resetDevice: (deviceId) =>
    set((state) => {
      if (!(deviceId in state.devices)) {
        return state;
      }
      const next = { ...state.devices };
      delete next[deviceId];
      return { devices: next };
    }),
}));

export function selectCableTestState(deviceId: number) {
  return (state: CableTestStoreState): {
    portMode: CableTestPortMode;
    priority: number;
    customPorts: number[];
    lastAction?: CableTestActionEntry;
  } => {
    const deviceState = state.devices[deviceId] ?? DEFAULT_STATE;
    return {
      portMode: deviceState.portMode,
      priority: deviceState.priority,
      customPorts: deviceState.customPorts,
      lastAction: deviceState.lastAction,
    };
  };
}

